import { RefObject, useCallback, useMemo } from 'react';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import toast from 'react-hot-toast';
import { setIsSending } from '../Store/features/chat/chatSlice';
import { chatService } from '../Services/chatService';
import { setFileDlpModalState } from '../Store/features/environment/environmentSlice';
import { DLPState } from '../Types/Enums';
import { isFileTypeAllowed } from '../utils/fileValidation';

const useClipboard = ({
    setTmpFileNames,
    setFiles,
    setNewMessage,
    textInputRef,
    fileTypes,
    allowedMimeTypes,
}: {
    setTmpFileNames: React.Dispatch<React.SetStateAction<any[]>>;
    setFiles: React.Dispatch<React.SetStateAction<any[]>>;
    setNewMessage: React.Dispatch<React.SetStateAction<string>>;
    textInputRef: RefObject<HTMLTextAreaElement | null>;
    fileTypes: { [key: string]: string[] };
    allowedMimeTypes?: string[];
}) => {
    const dispatch = useAppDispatch();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);

    const setCursorPosition = useCallback((textarea: HTMLTextAreaElement, position: number) => {
        textarea.setSelectionRange(position, position);
        textarea.focus();
    }, []);

    const handlePaste = useCallback(
        (
            event: React.ClipboardEvent,
            newMessage: string,
            combinedDlp: DLPState | null,
            fileTypes: { [key: string]: string[] },
            chatUploadEnabled: boolean = true,
        ) => {
            event.preventDefault();

            const plainText = event.clipboardData.getData('text/plain');
            if (plainText && textInputRef.current !== null) {
                const text = event.clipboardData.getData('text/plain');
                const selectionStart = textInputRef.current.selectionStart;
                const selectionEnd = textInputRef.current.selectionEnd;

                const newText = newMessage.slice(0, selectionStart || 0) + text + newMessage.slice(selectionEnd || 0);

                setNewMessage(newText);
                const newCaretPosition = (selectionStart || 0) + text.length;

                setTimeout(() => {
                    if (textInputRef.current !== null) {
                        setCursorPosition(textInputRef.current, newCaretPosition);
                    }
                }, 0);
            } else if (chatUploadEnabled) {
                const items = event.clipboardData.items;
                let isFilePasted = false;
                let allFiles: any[] = [];
                const uploadPromises: Promise<any>[] = [];

                for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    if (item.kind === 'file') {
                        isFilePasted = true;
                        dispatch(setIsSending(true));
                        const file = item.getAsFile();
                        if (file) {
                            const maxFileSize = 64 * 1024 * 1024;
                            if (file.size <= maxFileSize) {
                                if (!isFileTypeAllowed(file, fileTypes, allowedMimeTypes)) {
                                    toast.error(
                                        i18n.translate({
                                            id: 'toast.fileNotAllowed',
                                            sourceName: 'Chat',
                                            params: { fileName: file.name },
                                        }),
                                    );
                                    continue;
                                }
                                const uploadPromise = new Promise((resolve, reject) => {
                                    const formData = new FormData();
                                    formData.append('file', file);

                                    chatService
                                        .uploadFile(formData)
                                        .then((response: any) => {
                                            if (response.data.error) {
                                                toast.error(response.data.error);
                                                reject(response.data.error);
                                                return;
                                            }
                                            allFiles.push(response.data);
                                            resolve(response.data);
                                        })
                                        .catch((error) => {
                                            toast.error(
                                                i18n.translate({
                                                    id: 'toast.uploadError',
                                                    sourceName: 'Chat',
                                                    params: { fileName: file.name, errorMessage: error.message },
                                                }),
                                            );
                                            reject(error);
                                        });
                                });
                                uploadPromises.push(uploadPromise);
                            } else {
                                toast.error(
                                    i18n.translate({
                                        id: 'toast.fileTooLarge',
                                        sourceName: 'Chat',
                                        params: { fileName: file.name },
                                    }),
                                );
                            }
                        }
                    }
                }

                if (isFilePasted && uploadPromises.length > 0) {
                    Promise.all(uploadPromises)
                        .then(() => {
                            if (allFiles.length > 0) {
                                if (combinedDlp === DLPState.dlp_optional || combinedDlp === DLPState.dlp_on) {
                                    dispatch(
                                        setFileDlpModalState({
                                            state: 'open',
                                            files: allFiles,
                                            combinedDlp: combinedDlp,
                                            callback: (files: any[]) => {
                                                setFiles((prevFiles: any) => [...prevFiles, ...files]);
                                                setTmpFileNames((prevTmpFileNames: any) => [
                                                    ...prevTmpFileNames,
                                                    ...files.map((file) => file.name),
                                                ]);
                                            },
                                        }),
                                    );
                                } else {
                                    setFiles((prevFiles: any) => [...prevFiles, ...allFiles]);
                                    setTmpFileNames((prevTmpFileNames: any) => [
                                        ...prevTmpFileNames,
                                        ...allFiles.map((file) => file.name),
                                    ]);
                                }
                            }
                        })
                        .catch((error) => {
                            console.error('Fehler beim Hochladen der Dateien:', error);
                        })
                        .finally(() => {
                            dispatch(setIsSending(false));
                        });
                } else {
                    dispatch(setIsSending(false));
                }
            }
        },
        [
            setCursorPosition,
            dispatch,
            setTmpFileNames,
            setFiles,
            textInputRef,
            setNewMessage,
            i18n,
            fileTypes,
            allowedMimeTypes,
        ],
    );

    const ClipboardActions = useMemo(
        () => ({
            handlePaste,
            setCursorPosition,
        }),
        [handlePaste, setCursorPosition],
    );

    return { ClipboardActions };
};

export default useClipboard;
